import { calculateDistance } from "../../../functions/Geo";
import {fetch as fetchAirport } from "../../../services/airports/useAirports";
import {fetch as fetchAircraft } from "../../../services/aircraft/useAircrafts";
import __ from "lodash";

const getAirports = async (cargo, queryClient) => {

  const origins = {};
  const destinations = {};

  for (let i = 0; i < cargo.length; i++) {
    let {originAirportCode, destinationAirportCode} = cargo[i];

    if (!__.has(origins, originAirportCode)) {
      const airport = await fetchAirport({queryClient, extraKeys: [originAirportCode], useCache: true});
      if (Array.isArray(airport) && airport.length > 0) origins[originAirportCode] = (airport[0]); 
    }

    if (!__.has(destinations, destinationAirportCode)) {
      const airport = await fetchAirport({queryClient, extraKeys: [destinationAirportCode], useCache: true});
      if (Array.isArray(airport) && airport.length > 0) destinations[destinationAirportCode] = (airport[0]); 
    }

  }
  return {origins, destinations};
}

const getAirportsForRoute = async (route, queryClient) => {

  const airports = {};

  for (let i = 0; i < route.length; i++) {
    let airportCode = route[i];

    if (!__.has(airports, airportCode)) {
      const airport = await fetchAirport({queryClient, extraKeys: [airportCode], useCache: true});
      if (Array.isArray(airport) && airport.length > 0) airports[airportCode] = (airport[0]); 
    }

  }
  return airports;
}

const getAircraft = async (aircraftTailNumber, queryClient) => {
  const aircrafts = await fetchAircraft({queryClient, extraKeys: [], useCache: true});
  const aircraft = __.find(aircrafts, {aircraftTailNumber});
  return aircraft;
}

function buildRoute(combination, origins, destinations, aircraftOffset, routeCargo) {
  let points = [];
  let distance = 0;
  let distances = [];
  let cargo = 0;
  const origin = combination[0];
  let originAirport = origins[origin];
  points.push({ lat: originAirport.latitude, long: originAirport.longitude });

  for (let i = 1; i < combination.length; i++) {
    const destinationAirportCode = combination[i];
    let destinationAirport = destinations[destinationAirportCode];
    if (!destinationAirport)
      destinationAirport = origins[destinationAirportCode];
    points.push({ lat: destinationAirport.latitude, long: destinationAirport.longitude });
    const legDistance = calculateDistance({ lat: originAirport.latitude, long: originAirport.longitude }, { lat: destinationAirport.latitude, long: destinationAirport.longitude });
    distances.push(legDistance);
    distance += legDistance;
    cargo += legDistance * (aircraftOffset + routeCargo[combination.join('-')][i]);
    originAirport = destinationAirport;
  }
  return { distance, combination, cargo, payloads: routeCargo[combination.join('-')], points, distances, legCount: combination.length };
}

function buildCargoCarried(combination, cargo, aircraft) {
  let cargoCarried = new Array(combination.length).fill(0);
  let viable = true;
  cargo.forEach((consignment) => {
    const { originAirportCode, destinationAirportCode } = consignment;
    const originPos = combination.indexOf(originAirportCode);
    if (originPos < 0) viable = false;
    // console.log(combination.slice(originPos + 1));
    const destinationPos = combination.slice(originPos + 1).indexOf(destinationAirportCode);
    if (destinationPos < 0) viable = false;
    for (let i = 0; i <= destinationPos; i++) {
      cargoCarried[originPos + i] += (consignment.payloadLoose + consignment.payloadUnitised + consignment.payloadUld);
      if (cargoCarried[originPos + i] > aircraft.payload) viable = false;
    }
  });
  return { viable, cargoCarried };
}

const calculateOptimalRoute = async (aircraftTailNumber, cargo, queryClient, eventBus, eventPrefix, finalDestinationAirport) => {

  let completed = 0;
  const progressText = [];

  const updateProgress = (completedValue, text, replaceLastText) => {
    return new Promise((resolve) => {
      completed = completedValue;
      if (replaceLastText) {
        progressText[progressText.length - 1] = (text);
      } else {
        progressText.push(text);
      }
      eventBus.trigger(`${eventPrefix}.calculateRouteProgress`, {progress: {completed, progressText: progressText.join('\n')}});
      setTimeout(()=>{resolve()}, 15);  
    })
  }

  await updateProgress(0, 'Getting Aircraft Data');
  const aircraft = await getAircraft(aircraftTailNumber, queryClient);
  
  await updateProgress(5, 'Getting Airport Data');
  const {origins, destinations} = await getAirports(cargo, queryClient);
  if (finalDestinationAirport) {
    if (!__.has(destinations, finalDestinationAirport.airportCode)) destinations[finalDestinationAirport.airportCode] = finalDestinationAirport;
  }
  const originHubs = [];
  const destinationHubs = [];
  const airports = [];

  await updateProgress(10, 'Identifying Hubs');
  Object.entries(origins).forEach(([airportCode, airport]) => {
    if (airport.isHub) {
      originHubs.push(airportCode);
    } else {
      if (airports.indexOf(airportCode) < 0) airports.push(airportCode);
    }
  }); 

  Object.entries(destinations).forEach(([airportCode, airport]) => {
    if (airport.isHub) {
      destinationHubs.push(airportCode);
    } else {
      if (airports.indexOf(airportCode) < 0) airports.push(airportCode);
    }
  }); 

  let combinations = [];
  let combinationStrings = new Set();

  await updateProgress(15, 'Building Route Permutations ... 0');
  const originAirports = Object.entries(origins).map(([key, origin]) => origin.airportCode);
  const destinationAirports = Object.entries(destinations).map(([key, destination]) => destination.airportCode);
  const routeAirports = [...__.uniq(originAirports), ...__.uniq(destinationAirports)];

  const permute = (arr, memo = [], depth) => {
    if (arr.length === 0 || depth > 8 || memo.length > (airports.length + 2)) {
      if (!combinationStrings.has(memo.join('-')) && (!finalDestinationAirport || memo[memo.length -1] === finalDestinationAirport.airportCode)) {
        combinations.push(memo);
        combinationStrings.add(memo.join('-'));
      }
    } else {
      let used = new Set();
      for (let i = 0; i < arr.length; i++) {
        if (used.has(arr[i])) {
          continue;
        }
        used.add(arr[i]);
        let curr = arr.slice();
        let next = curr.splice(i, 1);
        if (memo[memo.length - 1] === next[0]) continue;
        permute(curr.slice(), memo.concat(next), depth + 1);
        permute(curr.slice(), memo, depth + 1);
      }
    }
  }
  const permuteStart = async (arr, memo = [], depth) => {
    if (arr.length === 0 && !combinationStrings.has(memo.join('-')) && (!finalDestinationAirport || memo[memo.length -1] === finalDestinationAirport.airportCode)) {
      combinations.push(memo);
      combinationStrings.add(memo.join('-'));
    } else {
      let used = new Set();
      for (let i = 0; i < arr.length; i++) {
        if (used.has(arr[i])) {
          continue;
        }
        used.add(arr[i]);
        let curr = arr.slice();
        let next = curr.splice(i, 1);
        if (memo[memo.length - 1] === next[0]) continue;
        permute(curr.slice(), memo.concat(next), depth + 1);
        permute(curr.slice(), memo, depth + 1);
        await updateProgress(Math.round(15 + ((60 / arr.length) * (i + 1))), `Building Route Permutations ... ${combinations.length}`, true);
      }
    }
  }

  if (airports.length < 8) await permuteStart(routeAirports,[], 0);
  await updateProgress(50, `Building Route Permutations ... ${combinations.length}`, true);

  const uniqCombinations = (combinations);

  const routeCargo = {};
  let aircraftOffset = aircraft.MZFW - aircraft.payload;

  await updateProgress(75, `Checking for Viable Routes ... 0%`);

  let lastPercent = 0;
  const viableRoutes = __.filter(uniqCombinations, (combination) => {
    // if (Math.round((i / uniqCombinations.length) * 100) > lastPercent) {
    //   lastPercent = Math.round((i / uniqCombinations.length) * 100);
    //   await updateProgress(50 + Math.round((20 / 100) * lastPercent), `Checking for Viable Routes ... ${lastPercent}%`, true);
    // }
    let { viable, cargoCarried } = buildCargoCarried(combination, cargo, aircraft);
    if (!viable) return false;
    routeCargo[combination.join('-')] = cargoCarried;
    return true;
  });

  await updateProgress(80, `Checking for Viable Routes ... 100%`, true);

  await updateProgress(80, `Calculating distances`);
  let routes = [];

  function addRoute(combination) {
    const routeOption = buildRoute(combination, origins, destinations, aircraftOffset, routeCargo);
    routes.push(routeOption);
  }

  await updateProgress(90, `Checking for Hub to Hub routes`);
  const hubToHubCombinations = __.filter(viableRoutes, (combination) => {if (originHubs.indexOf(combination[0]) >= 0 && destinationHubs.indexOf(combination[combination.length - 1]) >= 0) return true});
  if (Array.isArray(hubToHubCombinations) && hubToHubCombinations.length > 0) {
    hubToHubCombinations.forEach(combination => {
      addRoute(combination);
    }); 
  } else {
    await updateProgress(92, `Checking for Hub onward routes`);
    const fromHubCombinations = __.filter(viableRoutes, (combination) => {if (originHubs.indexOf(combination[0]) >= 0) return true});
    if (Array.isArray(fromHubCombinations) && fromHubCombinations.length > 0) {
      fromHubCombinations.forEach(combination => {
        addRoute(combination);
      });   
    } else {
      await updateProgress(95, `Checking for routes`);
      viableRoutes.forEach(combination => {
        addRoute(combination);
      });   
    }
  }

  const shortestRoutes = __.sortBy(routes, ['distance']); 

  await updateProgress(97, `Sorting routes`);
  const sortedRoutes = __.sortBy(__.filter(routes, route => route.distance < shortestRoutes[0].distance + 1000000 && route.legCount <= shortestRoutes[0].legCount), ['legCount', 'cargo', 'distance']); 

  return sortedRoutes.slice(0,5);
}

const calculateOptionForRoute = async (aircraftTailNumber, cargo, queryClient, route) => {
  let routeCargo = {};
  const aircraft = await getAircraft(aircraftTailNumber, queryClient);
  if (!aircraft) return null;
  const airports = await getAirportsForRoute(route, queryClient);
  if (!airports) return null;
  let aircraftOffset = aircraft.MZFW - aircraft.payload;
  let { viable, cargoCarried } = buildCargoCarried(route, cargo, aircraft);
//  if (!viable) return false;
  routeCargo[route.join('-')] = cargoCarried;

  const routeOption = buildRoute(route, airports, airports, aircraftOffset, routeCargo);
  return routeOption;
}

export default calculateOptimalRoute;
export {calculateOptionForRoute};